import { Badge, Button, Modal, Select } from '@chopchop-se/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ReceiptListItem, ReceiptStatus } from '@warehouse/shared';
import { Plus } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { DataTable, type DataTableColumn } from '../../../components/DataTable';
import { PageHeader } from '../../../components/PageHeader';
import { useListState } from '../../../hooks/useListState';
import { usePermission } from '../../../auth/auth-store';
import { formatDateTime } from '../../../lib/format';
import { toastError, toastSuccess } from '../../../lib/notify';
import { useLiveQuery } from '../../../realtime/useLiveQuery';
import { purchaseOrdersApi, receiptsApi, receivingKeys } from '../api';
import { RECEIPT_STATUS_TONE } from '../format';
import { SupplierPicker } from '../components/Pickers';

const STATUSES: ReceiptStatus[] = ['open', 'closed'];

/** `/warehouse/receiving` — the sessions list plus "start a receipt". */
export function ReceiptsPage(): ReactNode {
  const { t } = useTranslation('receiving');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canReceive = usePermission('warehouse.goods.receive');
  const [starting, setStarting] = useState(false);

  const { state, setState, setFilter } = useListState({ defaultSort: 'createdAt', defaultDir: 'desc' });

  const query = useQuery({
    queryKey: receivingKeys.receipts(state),
    queryFn: () => receiptsApi.list(state),
  });
  useLiveQuery(['receipts'], ['receipts']);

  const columns: DataTableColumn<ReceiptListItem>[] = [
    {
      id: 'poNo',
      header: t('receipts.poNo'),
      sortField: 'poNo',
      cell: (row) =>
        row.poNo ? (
          <span className="font-mono text-ink">{row.poNo}</span>
        ) : (
          <span className="text-ink-muted">{t('receipts.blind')}</span>
        ),
    },
    { id: 'supplier', header: t('receipts.supplier'), sortField: 'supplierName', cell: (row) => row.supplierName },
    {
      id: 'status',
      header: t('receipts.status'),
      sortField: 'status',
      cell: (row) => (
        <Badge tone={RECEIPT_STATUS_TONE[row.status]} dot minWidth>
          {t(`receipts.statuses.${row.status}`)}
        </Badge>
      ),
    },
    {
      id: 'lineCount',
      header: t('receipts.lineCount'),
      align: 'right',
      cell: (row) => row.lineCount,
    },
    {
      id: 'startedBy',
      header: t('receipts.startedBy'),
      hideBelow: 'lg',
      cell: (row) => row.startedByName ?? '—',
    },
    {
      id: 'createdAt',
      header: t('receipts.createdAt'),
      sortField: 'createdAt',
      cell: (row) => formatDateTime(row.createdAt),
    },
    {
      id: 'closedAt',
      header: t('receipts.closedAt'),
      sortField: 'closedAt',
      hideBelow: 'xl',
      cell: (row) => (row.closedAt ? formatDateTime(row.closedAt) : '—'),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('receipts.title')}
        subtitle={t('receipts.subtitle')}
        actions={
          canReceive ? (
            <Button
              icon={<Plus className="size-4" />}
              onClick={() => {
                setStarting(true);
              }}
            >
              {t('receipts.new')}
            </Button>
          ) : null
        }
      />

      <DataTable
        columns={columns}
        rows={query.data?.items ?? []}
        rowKey={(row) => row.id}
        state={state}
        onStateChange={setState}
        total={query.data?.total ?? null}
        loading={query.isLoading}
        error={query.error}
        searchPlaceholder={t('receipts.searchPlaceholder')}
        emptyTitle={t('receipts.empty')}
        emptyDescription={t('receipts.emptyHint')}
        onRowClick={(row) => {
          void navigate(`/warehouse/receiving/${row.id}`);
        }}
        filters={
          <Select
            aria-label={t('receipts.filterStatus')}
            options={[
              { value: '', label: t('receipts.filterStatus') },
              ...STATUSES.map((status) => ({ value: status, label: t(`receipts.statuses.${status}`) })),
            ]}
            value={String(state.filters?.status ?? '')}
            onChange={(event) => {
              setFilter('status', event.target.value || undefined);
            }}
          />
        }
      />

      {starting ? (
        <StartReceiptModal
          onClose={() => {
            setStarting(false);
          }}
          onStarted={async (receiptId) => {
            setStarting(false);
            await queryClient.invalidateQueries({ queryKey: ['receipts'] });
            void navigate(`/warehouse/receiving/${receiptId}`);
          }}
        />
      ) : null}
    </>
  );
}

/**
 * Two ways in: against an open purchase order (the normal case — it gives the
 * session its auto-filtered lines) or blind against a supplier, for deliveries
 * nobody ordered in the system.
 */
function StartReceiptModal({
  onClose,
  onStarted,
}: {
  onClose: () => void;
  onStarted: (receiptId: string) => void | Promise<void>;
}): ReactNode {
  const { t } = useTranslation('receiving');
  const [mode, setMode] = useState<'po' | 'blind'>('po');
  const [poId, setPoId] = useState('');
  const [supplierId, setSupplierId] = useState<string | null>(null);

  const openOrders = useQuery({
    queryKey: ['purchase-orders', 'receivable'],
    queryFn: () =>
      purchaseOrdersApi.list({ page: 1, pageSize: 100, filters: { receivable: true }, sort: 'poNo' }),
  });

  const start = useMutation({
    mutationFn: () =>
      receiptsApi.open(mode === 'po' ? { poId } : { supplierId }),
    onSuccess: async (receipt) => {
      toastSuccess(t('receipts.started'));
      await onStarted(receipt.id);
    },
    onError: (error) => {
      toastError(error);
    },
  });

  return (
    <Modal open onClose={onClose} title={t('receipts.newTitle')} width={520}>
      <div className="space-y-4">
        <div className="flex gap-2">
          <Button
            variant={mode === 'po' ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => {
              setMode('po');
            }}
          >
            {t('receipts.againstPo')}
          </Button>
          <Button
            variant={mode === 'blind' ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => {
              setMode('blind');
            }}
          >
            {t('receipts.blind')}
          </Button>
        </div>

        {mode === 'po' ? (
          <Select
            label={t('receipts.poNo')}
            placeholder={t('receipts.poPlaceholder')}
            searchable
            options={(openOrders.data?.items ?? []).map((po) => ({
              value: po.id,
              label: `${po.poNo} · ${po.supplierName}`,
            }))}
            value={poId}
            onChange={(event) => {
              setPoId(event.target.value);
            }}
          />
        ) : (
          <SupplierPicker
            label={t('receipts.supplier')}
            value={supplierId}
            onChange={(supplier) => {
              setSupplierId(supplier?.id ?? null);
            }}
          />
        )}

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            {t('session.back')}
          </Button>
          <Button
            loading={start.isPending}
            disabled={mode === 'po' ? poId.length === 0 : !supplierId}
            onClick={() => {
              start.mutate();
            }}
          >
            {t('receipts.start')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
